import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { MoonLoader } from "@/components/ui/moon-loader"
import { toast } from "sonner"
import { useQuery } from '@tanstack/react-query'
import { payrollApi } from '@/lib/api/payroll'
import { getCompanyId } from '@/lib/config'
import {
  BarChart3,
  Download,
  FileText,
  TrendingUp,
  DollarSign,
  Users,
  Calendar,
  Filter,
  RefreshCw,
  Eye,
  Calculator,
  Target,
  Zap,
  Shield,
  Star,
  ArrowRight,
  Check,
  X,
  AlertTriangle,
  Info,
  Settings,
  Bell,
  ArrowRightLeft,
  PieChart,
  Activity,
  Thermometer,
  User,
  Phone,
  Mail,
  MapPin,
  Building2,
  UserCheck,
  HelpCircle,
  Home,
  ShoppingCart,
  ShoppingBag,
  Banknote,
  Receipt,
  Wallet,
  Package,
  BookOpen,
  MessageSquare,
  FolderOpen,
  Building,
  Settings as SettingsIcon,
  HelpCircle as HelpCircleIcon,
  Brain as BrainIcon,
  Zap as ZapIcon,
  BarChart3 as BarChart3Icon,
  Activity as ActivityIcon,
  Calculator as CalculatorIcon,
  Loader2,
  Receipt as ReceiptIcon,
  Clock,
  Plus,
  Edit,
  Trash2,
  MoreHorizontal,
  Copy,
  Save,
  Send,
  Play,
  Pause,
  RotateCcw,
  Search
} from "lucide-react"

interface ReportData {
  summary?: {
    totalEmployees: number
    totalGrossPay: number
    totalNetPay: number
    totalTaxes: number
    totalDeductions: number
    totalBenefits: number
  }
  employees?: Array<{
    id: string
    name: string
    department: string
    grossPay: number
    netPay: number
    taxes: number
    deductions: number
    benefits: number
  }>
  departments?: Array<{
    department: string
    employeeCount: number
    totalGrossPay: number
    totalNetPay: number
  }>
}

export function PayrollReports() {
  const [selectedReportType, setSelectedReportType] = useState('summary')
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  })
  const [selectedDepartment, setSelectedDepartment] = useState('')
  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const companyId = getCompanyId()

  // Set default date range to current month
  useEffect(() => {
    const now = new Date()
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    
    setDateRange({
      startDate: firstDay.toISOString().split('T')[0],
      endDate: lastDay.toISOString().split('T')[0]
    })
  }, [])

  const reportTypes = [
    { value: 'summary', label: 'Payroll Summary', description: 'Overview of payroll costs and statistics' },
    { value: 'detailed', label: 'Detailed Payroll', description: 'Employee-by-employee payroll breakdown' },
    { value: 'department', label: 'Department Report', description: 'Payroll costs by department' },
    { value: 'tax_report', label: 'Tax Report', description: 'Tax withholdings and contributions' },
    { value: 'overtime', label: 'Overtime Report', description: 'Overtime hours and costs analysis' },
    { value: 'year_end', label: 'Year-End Report', description: 'Annual payroll summary for tax filing' }
  ]

  // Fetch employees for department filtering
  const { data: employees } = useQuery({
    queryKey: ['payroll-employees', companyId],
    queryFn: () => payrollApi.getEmployees({}),
    enabled: !!companyId
  })

  const departments = Array.from(new Set(employees?.employees?.map(emp => emp.department).filter(Boolean))) as string[] || []

  const handleGenerateReport = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      toast.error('Please select start and end dates')
      return
    }

    try {
      setIsGenerating(true)
      
      console.log('🔍 Generating report with params:', {
        selectedReportType,
        dateRange,
        selectedDepartment
      })
      
      // Fetch payroll periods in the date range
      const periods = await payrollApi.getPayrollPeriods({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      })

      console.log('📅 Fetched periods:', periods)

      // Fetch employees
      const employeesData = await payrollApi.getEmployees({})
      
      console.log('👥 Fetched employees:', employeesData)
      
      // Process data based on report type
      let processedData: ReportData = {}

      switch (selectedReportType) {
        case 'summary':
          processedData = generateSummaryReport(periods.payrollPeriods || [])
          break
        case 'detailed':
          processedData = generateDetailedReport(periods.payrollPeriods || [], employeesData.employees || [])
          break
        case 'department':
          processedData = generateDepartmentReport(periods.payrollPeriods || [], employeesData.employees || [])
          break
        default:
          processedData = generateSummaryReport(periods.payrollPeriods || [])
      }

      console.log('📊 Processed data:', processedData)
      setReportData(processedData)
      toast.success('Report generated successfully')
    } catch (error) {
      console.error('Error generating report:', error)
      toast.error('Failed to generate report')
    } finally {
      setIsGenerating(false)
    }
  }

  const generateSummaryReport = (periods: any[]) => {
    console.log('📊 Generating summary report for periods:', periods)
    
    const summary = periods.reduce((acc, period) => {
      console.log('Processing period:', period.periodName, {
        employeeCount: period.employeeCount,
        totalGrossPay: period.totalGrossPay,
        totalNetPay: period.totalNetPay,
        totalTaxes: period.totalTaxes,
        totalDeductions: period.totalDeductions,
        totalBenefits: period.totalBenefits
      })
      
      acc.totalEmployees += period.employeeCount || 0
      acc.totalGrossPay += Number(period.totalGrossPay || 0)
      acc.totalNetPay += Number(period.totalNetPay || 0)
      acc.totalTaxes += Number(period.totalTaxes || 0)
      acc.totalDeductions += Number(period.totalDeductions || 0)
      acc.totalBenefits += Number(period.totalBenefits || 0)
      return acc
    }, {
      totalEmployees: 0,
      totalGrossPay: 0,
      totalNetPay: 0,
      totalTaxes: 0,
      totalDeductions: 0,
      totalBenefits: 0
    })

    console.log('📊 Final summary:', summary)
    return { summary }
  }

  const generateDetailedReport = (periods: any[], employees: any[]) => {
    const employeeReports = employees.map(employee => {
      const employeePeriods = periods.filter(period => 
        period.payrollRecords?.some((record: any) => record.employeeId === employee.id)
      )
      
      const totals = employeePeriods.reduce((acc, period) => {
        const record = period.payrollRecords?.find((r: any) => r.employeeId === employee.id)
        if (record) {
          acc.grossPay += Number(record.grossPay || 0)
          acc.netPay += Number(record.netPay || 0)
          acc.taxes += Number(record.federalTax || 0) + Number(record.stateTax || 0) + Number(record.socialSecurity || 0) + Number(record.medicare || 0)
          acc.deductions += Number(record.unionDues || 0) + Number(record.parkingFees || 0) + Number(record.loanRepayment || 0)
          acc.benefits += Number(record.healthInsurance || 0) + Number(record.dentalInsurance || 0) + Number(record.retirement401k || 0)
        }
        return acc
      }, {
        grossPay: 0,
        netPay: 0,
        taxes: 0,
        deductions: 0,
        benefits: 0
      })

      return {
        id: employee.id,
        name: `${employee.firstName} ${employee.lastName}`,
        department: employee.department || 'N/A',
        ...totals
      }
    })

    return { employees: employeeReports }
  }

  const generateDepartmentReport = (periods: any[], employees: any[]) => {
    const departmentMap = new Map()

    employees.forEach(employee => {
      const department = employee.department || 'N/A'
      if (!departmentMap.has(department)) {
        departmentMap.set(department, {
          department,
          employeeCount: 0,
          totalGrossPay: 0,
          totalNetPay: 0
        })
      }
      
      const deptData = departmentMap.get(department)
      deptData.employeeCount++

      // Calculate totals for this employee across all periods
      periods.forEach(period => {
        const record = period.payrollRecords?.find((r: any) => r.employeeId === employee.id)
        if (record) {
          deptData.totalGrossPay += Number(record.grossPay || 0)
          deptData.totalNetPay += Number(record.netPay || 0)
        }
      })
    })

    return { departments: Array.from(departmentMap.values()) }
  }

  const handleExportReport = async () => {
    if (!reportData) {
      toast.error('Please generate a report first')
      return
    }

    try {
      setIsExporting(true)
      
      // Create CSV content based on report type
      let csvContent = ''
      let filename = ''

      switch (selectedReportType) {
        case 'summary':
          csvContent = generateSummaryCSV(reportData.summary!)
          filename = `payroll-summary-${dateRange.startDate}-to-${dateRange.endDate}.csv`
          break
        case 'detailed':
          csvContent = generateDetailedCSV(reportData.employees!)
          filename = `payroll-detailed-${dateRange.startDate}-to-${dateRange.endDate}.csv`
          break
        case 'department':
          csvContent = generateDepartmentCSV(reportData.departments!)
          filename = `payroll-department-${dateRange.startDate}-to-${dateRange.endDate}.csv`
          break
        default:
          csvContent = generateSummaryCSV(reportData.summary!)
          filename = `payroll-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`
      }

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      toast.success('Report exported successfully')
    } catch (error) {
      console.error('Error exporting report:', error)
      toast.error('Failed to export report')
    } finally {
      setIsExporting(false)
    }
  }

  const generateSummaryCSV = (summary: any) => {
    return `Metric,Amount
Total Employees,${summary.totalEmployees}
Total Gross Pay,$${summary.totalGrossPay.toFixed(2)}
Total Net Pay,$${summary.totalNetPay.toFixed(2)}
Total Taxes,$${summary.totalTaxes.toFixed(2)}
Total Deductions,$${summary.totalDeductions.toFixed(2)}
Total Benefits,$${summary.totalBenefits.toFixed(2)}`
  }

  const generateDetailedCSV = (employees: any[]) => {
    const headers = 'Employee Name,Department,Gross Pay,Net Pay,Taxes,Deductions,Benefits'
    const rows = employees.map(emp => 
      `${emp.name},${emp.department},$${emp.grossPay.toFixed(2)},$${emp.netPay.toFixed(2)},$${emp.taxes.toFixed(2)},$${emp.deductions.toFixed(2)},$${emp.benefits.toFixed(2)}`
    )
    return [headers, ...rows].join('\n')
  }

  const generateDepartmentCSV = (departments: any[]) => {
    const headers = 'Department,Employee Count,Total Gross Pay,Total Net Pay'
    const rows = departments.map(dept => 
      `${dept.department},${dept.employeeCount},$${dept.totalGrossPay.toFixed(2)},$${dept.totalNetPay.toFixed(2)}`
    )
    return [headers, ...rows].join('\n')
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  return (
    <div className="space-y-6">
      {/* Report Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Payroll Reports</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="reportType">Report Type</Label>
              <Select value={selectedReportType} onValueChange={setSelectedReportType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  {reportTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-1">
                {reportTypes.find(t => t.value === selectedReportType)?.description}
              </p>
            </div>
            <div>
              <Label htmlFor="department">Department</Label>
              <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                <SelectTrigger>
                  <SelectValue placeholder="All departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((dept, index) => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              />
            </div>
          </div>
          <div className="flex space-x-2">
            <Button onClick={handleGenerateReport} disabled={isGenerating} className="flex items-center space-x-2">
              {isGenerating ? (
                <MoonLoader size="sm" color="blue" />
              ) : (
                <BarChart3 className="h-4 w-4" />
              )}
              <span>{isGenerating ? 'Generating...' : 'Generate Report'}</span>
            </Button>
            <Button variant="outline" onClick={handleExportReport} disabled={isExporting || !reportData} className="flex items-center space-x-2">
              {isExporting ? (
                <MoonLoader size="sm" color="blue" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              <span>{isExporting ? 'Exporting...' : 'Export'}</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Report Preview</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!reportData ? (
            <div className="text-center py-12">
              <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No report generated yet</h3>
              <p className="text-muted-foreground mb-4">
                Configure your report parameters and click "Generate Report" to see the results
              </p>
              <Button onClick={handleGenerateReport} disabled={isGenerating}>
                {isGenerating ? (
                  <MoonLoader size="sm" color="blue" />
                ) : (
                  <BarChart3 className="h-4 w-4 mr-2" />
                )}
                {isGenerating ? 'Generating...' : 'Generate Report'}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary Report */}
              {selectedReportType === 'summary' && reportData.summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-2">
                        <Users className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Total Employees</p>
                          <p className="text-2xl font-bold">{reportData.summary.totalEmployees}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-2">
                        <DollarSign className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Total Gross Pay</p>
                          <p className="text-2xl font-bold">{formatCurrency(reportData.summary.totalGrossPay)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-2">
                        <Calculator className="h-5 w-5 text-purple-600" />
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Total Net Pay</p>
                          <p className="text-2xl font-bold">{formatCurrency(reportData.summary.totalNetPay)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-2">
                        <Shield className="h-5 w-5 text-red-600" />
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Total Taxes</p>
                          <p className="text-2xl font-bold">{formatCurrency(reportData.summary.totalTaxes)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-2">
                        <Target className="h-5 w-5 text-orange-600" />
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Total Deductions</p>
                          <p className="text-2xl font-bold">{formatCurrency(reportData.summary.totalDeductions)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-2">
                        <Star className="h-5 w-5 text-yellow-600" />
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Total Benefits</p>
                          <p className="text-2xl font-bold">{formatCurrency(reportData.summary.totalBenefits)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Detailed Report */}
              {selectedReportType === 'detailed' && reportData.employees && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee Name</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Gross Pay</TableHead>
                        <TableHead>Net Pay</TableHead>
                        <TableHead>Taxes</TableHead>
                        <TableHead>Deductions</TableHead>
                        <TableHead>Benefits</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.employees.map((employee, index) => (
                        <TableRow key={employee.id || index}>
                          <TableCell className="font-medium">{employee.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{employee.department}</Badge>
                          </TableCell>
                          <TableCell>{formatCurrency(employee.grossPay)}</TableCell>
                          <TableCell>{formatCurrency(employee.netPay)}</TableCell>
                          <TableCell>{formatCurrency(employee.taxes)}</TableCell>
                          <TableCell>{formatCurrency(employee.deductions)}</TableCell>
                          <TableCell>{formatCurrency(employee.benefits)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Department Report */}
              {selectedReportType === 'department' && reportData.departments && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Department</TableHead>
                        <TableHead>Employee Count</TableHead>
                        <TableHead>Total Gross Pay</TableHead>
                        <TableHead>Total Net Pay</TableHead>
                        <TableHead>Average Gross Pay</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.departments.map((dept, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{dept.department}</TableCell>
                          <TableCell>{dept.employeeCount}</TableCell>
                          <TableCell>{formatCurrency(dept.totalGrossPay)}</TableCell>
                          <TableCell>{formatCurrency(dept.totalNetPay)}</TableCell>
                          <TableCell>{formatCurrency(dept.totalGrossPay / dept.employeeCount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Export Button */}
              <div className="flex justify-end">
                <Button onClick={handleExportReport} disabled={isExporting} className="flex items-center space-x-2">
                  {isExporting ? (
                    <MoonLoader size="sm" color="blue" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  <span>{isExporting ? 'Exporting...' : 'Export Report'}</span>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Reports */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card 
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => {
            setSelectedReportType('summary')
            handleGenerateReport()
          }}
        >
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium">Payroll Summary</h4>
                <p className="text-sm text-muted-foreground">Current period overview</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => {
            setSelectedReportType('detailed')
            handleGenerateReport()
          }}
        >
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium">Employee Report</h4>
                <p className="text-sm text-muted-foreground">Individual employee details</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <Calculator className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium">Tax Report</h4>
                <p className="text-sm text-muted-foreground">Tax withholdings summary</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => {
            setSelectedReportType('department')
            handleGenerateReport()
          }}
        >
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <Building2 className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h4 className="font-medium">Department Report</h4>
                <p className="text-sm text-muted-foreground">Costs by department</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Clock className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h4 className="font-medium">Overtime Report</h4>
                <p className="text-sm text-muted-foreground">Overtime analysis</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                <Calendar className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h4 className="font-medium">Year-End Report</h4>
                <p className="text-sm text-muted-foreground">Annual payroll summary</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
